import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Input, Modal, Tooltip } from 'antd';
import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useLayerProperitesContext, ColorRamp } from '../../../../context/LayerPropertiesContext';
import Notification from '../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../enums/notificationTypes';
import SymbologySwatch from './components/SymbologySwatch';
import CategorySymbologyEditor from './CategorySymbologyEditor';
import ColorRampSelect from './components/ColorRampSelect';
import HintLabel from '../../components/HintLabel';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { useAuth } from '../../../../context/AuthContext';
import { LayerAction } from '../../../../enums/mapPortalActions';
import { layerActionAccess } from '../../constants';
import { ClassBreakType, QuantityClass, QuantitySymbologyConfig, SingleSymbologyConfig } from '../../../../types/symbology.types';
import { buildSingleConfig } from './symbologyConfig.utils';
import {
  defaultQuantityConfig,
  generateAutoClasses,
  generateManualClasses,
  applyRampToClasses,
  parseRangeExpression,
  detectRangeConflict,
  buildExpression,
  buildLabel,
  MIN_CLASSES,
  MAX_CLASSES,
  MIN_CLASS_BREAKS_ERROR,
} from './quantityRange.utils';
import '../LayerProperties.scss';

const BREAK_TYPES: { value: ClassBreakType; label: string }[] = [
  { value: 'jenks', label: 'Jenks' },
  { value: 'equalInterval', label: 'Equal' },
  { value: 'quantile', label: 'Quantile' },
  { value: 'headsTails', label: 'Heads/Tails' },
  { value: 'manual', label: 'Manual' },
];

// US-101669 Scenario 5 / US-101670: Break Type definitions tooltip.
const BREAK_TYPE_HELP = (
  <div className="lp-quantity__help">
    The break type determines how the numeric values are split into classes.
    <ul>
      <li>
        <b>Jenks (Natural Breaks):</b> Groups values based on natural clusters in the data.
      </li>
      <li>
        <b>Equal Interval:</b> Divides the range into evenly sized value intervals.
      </li>
      <li>
        <b>Quantile:</b> Ensures each class contains the same number of features.
      </li>
      <li>
        <b>Heads/Tails:</b> Splits skewed data by repeatedly separating high values from the rest.
      </li>
      <li>
        <b>Manual:</b> User-defined class breaks based on custom criteria.
      </li>
    </ul>
  </div>
);

const RANGE_HELP = 'Use less than (<), greater than (>) or equal to (=) or a combination to specify what value range is included in this class.';

interface QuantitySymbologyProps {
  /** All layer fields; non-numeric fields are present but disabled (Scenario 2). */
  fieldOptions: { label: string; value: string; disabled?: boolean }[];
  features: Record<string, (string | number)[]>;
  onRequestExternalIcon: (source: 'styles' | 'upload') => Promise<{ key: string } | null>;
}

const QuantitySymbology: React.FC<QuantitySymbologyProps> = ({ fieldOptions, features, onRequestExternalIcon }) => {
  const ctx = useLayerProperitesContext();
  const { symbologyConfig, layerType, setState } = ctx;
  const isPointLayer = layerType === 'Point' || layerType === 'MultiPoint';
  // An explicit `disabled` outranks ConfigProvider's componentDisabled default.
  const { canAccess } = useAuth();
  const canEditLayer = canAccess(layerActionAccess(LayerAction.EDIT_LAYER_PROPERTIES));

  const config: QuantitySymbologyConfig = useMemo(
    () => (symbologyConfig && symbologyConfig.type === 'quantity' ? symbologyConfig : defaultQuantityConfig()),
    [symbologyConfig],
  );
  const baseSingleConfig: SingleSymbologyConfig = useMemo(() => buildSingleConfig(ctx), [ctx]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const numericValuesFor = (field: string): number[] =>
    ((features?.[field] ?? []) as (string | number)[]).filter((v) => typeof v === 'number' && !isNaN(v)) as number[];

  const numericValues = useMemo(() => numericValuesFor(config.field), [features, config.field]);

  const conflictError = useMemo(() => (config.breakType === 'manual' ? detectRangeConflict(config.classes) : null), [config]);

  const commit = (next: QuantitySymbologyConfig) => {
    setState((prev) => ({ ...prev, symbology: 'quantity', symbologyConfig: next, quantityField: next.field || undefined }));
  };

  const regenerate = (cfg: QuantitySymbologyConfig, values: number[]): QuantityClass[] => {
    if (!cfg.field) return [];
    return cfg.breakType === 'manual'
      ? generateManualClasses(cfg.numberOfClasses, cfg.colorRamp, baseSingleConfig)
      : generateAutoClasses(values, cfg.breakType, cfg.numberOfClasses, cfg.colorRamp, baseSingleConfig);
  };

  // US-101669 Scenario 11: changing the field clears + regenerates the table.
  // The value sample is fetched server-side (scales with layer size) and arrives
  // after the field is set, so we flag the field and let the effect below
  // regenerate once its values load.
  const pendingGenRef = useRef<string | null>(null);
  const handleFieldChange = (field: string) => {
    if (field === config.field) return;
    pendingGenRef.current = field;
    setEditingIndex(null);
    commit({ ...config, field, classes: [] });
  };

  // Regenerate classes once the freshly-selected field's sample arrives. Guarded
  // by pendingGenRef so loading a SAVED config never clobbers existing classes.
  useEffect(() => {
    if (!config.field || pendingGenRef.current !== config.field) return;
    // Manual breaks need no data; auto types (jenks/quantile/equal/headsTails) do.
    if (config.breakType !== 'manual' && numericValues.length === 0) return;
    pendingGenRef.current = null;
    commit({ ...config, classes: regenerate(config, numericValues) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.field, numericValues]);

  // US-101669 Scenario 4: selecting a break type refreshes the table.
  const handleBreakTypeChange = (breakType: ClassBreakType) => {
    const next = { ...config, breakType };
    setEditingIndex(null);
    commit({ ...next, classes: regenerate(next, numericValues) });
  };

  const handleNumberChange = (numberOfClasses: number) => {
    const next = { ...config, numberOfClasses };
    setEditingIndex(null);
    commit({ ...next, classes: regenerate(next, numericValues) });
  };

  // US-101669 Scenario 9: a new ramp recolours every class.
  const handleRampChange = (colorRamp: ColorRamp) => {
    commit({ ...config, colorRamp, classes: applyRampToClasses(config.classes, colorRamp) });
  };

  const updateClass = (index: number, patch: Partial<QuantityClass>) => {
    commit({ ...config, classes: config.classes.map((c, i) => (i === index ? { ...c, ...patch } : c)) });
  };

  // US-101670 Scenario 2: Manual range editing. Parse to numeric bounds when valid.
  const updateExpression = (index: number, raw: string) => {
    const parsed = parseRangeExpression(raw);
    const patch: Partial<QuantityClass> = { expression: raw };
    if (parsed.ok) {
      patch.min = parsed.min;
      patch.max = parsed.max;
      patch.minOp = parsed.minOp;
      patch.maxOp = parsed.maxOp;
    }
    updateClass(index, patch);
  };

  // US-101670 Scenarios 13 & 14: delete a class break — adjacent range absorbs
  // it; at least 2 must remain.
  const removeClass = (index: number) => {
    if (config.classes.length <= MIN_CLASSES) {
      Notification({ message: MIN_CLASS_BREAKS_ERROR, type: NotificationTypes.ERROR });
      return;
    }
    const classes = [...config.classes];
    const removed = classes[index];
    if (index + 1 < classes.length) {
      const next = classes[index + 1];
      const merged: QuantityClass = { ...next, min: removed.min, minOp: removed.minOp };
      merged.expression = buildExpression(merged.min, merged.max, merged.minOp, merged.maxOp);
      // Always re-derive the label from the merged range — the row's bounds just
      // changed, so the old label (describing the pre-merge range) is stale.
      merged.displayLabel = buildLabel(merged.min, merged.max);
      classes[index + 1] = merged;
    } else if (index - 1 >= 0) {
      const prev = classes[index - 1];
      const merged: QuantityClass = { ...prev, max: removed.max, maxOp: removed.maxOp };
      merged.expression = buildExpression(merged.min, merged.max, merged.minOp, merged.maxOp);
      merged.displayLabel = buildLabel(merged.min, merged.max);
      classes[index - 1] = merged;
    }
    classes.splice(index, 1);
    if (editingIndex === index) setEditingIndex(null);
    // Keep the NUMBER select in sync with the actual row count, so it reflects
    // reality and any later regenerate() (break type / field change) uses the
    // correct count instead of the stale pre-delete value.
    commit({ ...config, classes, numberOfClasses: classes.length });
  };

  const applyToClass = (index: number, symbology: SingleSymbologyConfig) => {
    updateClass(index, { symbology });
    setEditingIndex(null);
  };

  const applyToAll = (symbology: SingleSymbologyConfig) => {
    commit({ ...config, classes: config.classes.map((c) => ({ ...c, symbology: { ...symbology } })) });
    setEditingIndex(null);
  };

  const numberOptions = Array.from({ length: MAX_CLASSES - MIN_CLASSES + 1 }, (_, i) => MIN_CLASSES + i);
  const editingClass = editingIndex != null ? config.classes[editingIndex] : null;
  const isManual = config.breakType === 'manual';

  return (
    <div className="lp-symbology__stack">
      <div className="lp-symbology__card">
        {/* FIELD + BREAK TYPE */}
        <div className="lp-quantity__grid">
          <div className="lp-quantity__field">
            <span className="lp-quantity__label">FIELD</span>
            <Select className="lp-quantity__select" placeholder="Select Field" value={config.field || undefined} onChange={handleFieldChange}>
              {fieldOptions?.map((f) => (
                <Select.Option key={f.value} value={f.value} disabled={f.disabled}>
                  {f.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="lp-quantity__field">
            <span className="lp-quantity__label">
              BREAK TYPE{' '}
              <Tooltip title={BREAK_TYPE_HELP} overlayClassName="lp-quantity__help-overlay">
                <InfoCircleOutlined className="lp-quantity__info" />
              </Tooltip>
            </span>
            <Select
              className="lp-quantity__select"
              placeholder="Select"
              value={config.breakType}
              onChange={handleBreakTypeChange}
              options={BREAK_TYPES}
            />
          </div>
        </div>

        {/* NUMBER + RAMP */}
        <div className="lp-quantity__grid">
          <div className="lp-quantity__field">
            <span className="lp-quantity__label">NUMBER</span>
            <Select
              className="lp-quantity__select"
              value={config.numberOfClasses}
              onChange={handleNumberChange}
              options={numberOptions.map((n) => ({ value: n, label: String(n) }))}
            />
          </div>
          <div className="lp-quantity__field">
            <span className="lp-quantity__label">RAMP</span>
            <ColorRampSelect value={config.colorRamp} onChange={handleRampChange} bare />
          </div>
        </div>

        {/* Class break table */}
        {config.field && config.classes.length > 0 && (
          <div className="lp-unique__table">
            <div className="lp-quantity__row lp-unique__row--head">
              <span>Symbol</span>
              <HintLabel label="Range" hint={RANGE_HELP} />
              <span>Display</span>
              <span />
            </div>

            {config.classes.map((cls, index) => {
              const parsed = isManual ? parseRangeExpression(cls.expression) : null;
              const rangeError = parsed && !parsed.ok ? parsed.error : null;
              return (
                <div className="lp-quantity__row" key={index}>
                  <button type="button" className="lp-unique__symbol-btn" onClick={() => setEditingIndex(index)} title="Edit class symbology">
                    <SymbologySwatch symbology={cls.symbology} isPointLayer={isPointLayer} />
                  </button>

                  <span className="lp-quantity__cell">
                    <Input
                      value={cls.expression ?? ''}
                      disabled={!isManual || !canEditLayer}
                      status={rangeError ? 'error' : undefined}
                      placeholder={isManual ? 'e.g. >= 0 AND <= 10' : ''}
                      onChange={(e) => updateExpression(index, e.target.value)}
                    />
                    {rangeError && <div className="lp-unique__display-error">{rangeError}</div>}
                  </span>

                  <span className="lp-quantity__cell">
                    <Input
                      value={cls.displayLabel}
                      onChange={(e) => updateClass(index, { displayLabel: e.target.value })}
                      placeholder={cls.expression ?? ''}
                    />
                  </span>

                  <ConditionalRender visible={canEditLayer}>
                    <button type="button" className="lp-unique__remove-btn" onClick={() => removeClass(index)} title="Remove class break">
                      <CloseOutlined />
                    </button>
                  </ConditionalRender>
                </div>
              );
            })}

            {conflictError && <div className="lp-unique__display-error lp-quantity__conflict">{conflictError}</div>}
          </div>
        )}
      </div>

      {/* US-101670 Scenario 11: per-class Single symbology editor (modal). */}
      <Modal open={editingClass != null} onCancel={() => setEditingIndex(null)} footer={null} destroyOnClose width={560} centered>
        {editingClass && (
          <CategorySymbologyEditor
            key={editingIndex}
            title={editingClass.displayLabel.trim() || editingClass.expression || `Class ${editingIndex! + 1}`}
            value={editingClass.symbology}
            isPointLayer={isPointLayer}
            onRequestExternalIcon={onRequestExternalIcon}
            onApply={(cfg) => applyToClass(editingIndex!, cfg)}
            onApplyToAll={applyToAll}
          />
        )}
      </Modal>
    </div>
  );
};

export default QuantitySymbology;
